import {
  ENEDIS_LATE_AFTER_DAYS_DEFAULT,
  enedisLateness,
  type DataSeries,
  type DeliveryPoint,
} from "@syllarys/contracts";

export type CollectionStatus =
  "syncing" | "healthy" | "delayed" | "missing" | "inactive";

const DAY_MS = 86_400_000;

/** Séries publiées au mois ou par période (facturantes, relevés GRDF). */
function periodic(series: DataSeries): boolean {
  return series.granularity === "month" || series.granularity === "period";
}

/**
 * État de collecte d'un point d'après sa série la plus fraîche. Un PRM
 * Enedis alimenté au jour ou plus finement suit la règle du module : en
 * retard à `ENEDIS_LATE_AFTER_DAYS_DEFAULT` journées dues manquantes
 * (`enedisLateness`), comme le bilan, ses alertes et l'écran Connecteurs.
 * Les relevés GRDF (PCE) et les séries mensuelles, publiés bien plus
 * lentement, gardent un délai de 45 jours.
 */
export function collectionStatus(
  point: DeliveryPoint,
  series: DataSeries | undefined,
  syncing: boolean,
  now = Date.now(),
): CollectionStatus {
  if (syncing) return "syncing";
  if (point.status === "inactive") return "inactive";
  if (!series) return "missing";
  const freshAt = Date.parse(series.freshAt);
  if (!Number.isFinite(freshAt)) return "missing";
  const gas = point.type === "PCE" || point.provider === "GRDF";
  if (gas || periodic(series)) {
    return now - freshAt > 45 * DAY_MS ? "delayed" : "healthy";
  }
  return enedisLateness(
    new Date(freshAt),
    new Date(now),
    ENEDIS_LATE_AFTER_DAYS_DEFAULT,
  ).late
    ? "delayed"
    : "healthy";
}

/**
 * Série qui dit l'état de collecte d'un point : une série réelle avant une
 * synthétique, puis la plus fraîche (la courbe ou l'énergie d'un PRM plutôt
 * que sa série facturante mensuelle).
 */
export function collectionSeries(
  candidates: readonly DataSeries[],
): DataSeries | undefined {
  let preferred: DataSeries | undefined;
  for (const series of candidates) {
    if (
      !preferred ||
      (preferred.synthetic && !series.synthetic) ||
      (preferred.synthetic === series.synthetic &&
        Date.parse(series.freshAt) > Date.parse(preferred.freshAt))
    ) {
      preferred = series;
    }
  }
  return preferred;
}

export function displayPointLabel(label: string): string {
  return label.trim() || "—";
}

/** Nom d'affichage uniquement : ne modifie jamais le libellé métier enregistré. */
export function deliveryPointDisplayName(
  point: Pick<DeliveryPoint, "label" | "type" | "reference">,
): string {
  const type = point.type === "internal" ? "Compteur interne" : point.type;
  const identity = `${type} ${point.reference.trim() || "sans référence"}`;
  const label = point.label.trim();
  return label && label !== "—" && label !== identity
    ? `${label} · ${identity}`
    : identity;
}
